/**
 * The search stream's state machine, without the hook around it (`KLQ-20260905-001` §5a).
 *
 * Lifted out of `useSearchStream.ts` unchanged so the two guards that keep a superseded query
 * off the screen are a pure function a test can drive — ADR-0015 keeps vitest to pure logic,
 * with no DOM to render a hook in.
 */

import type { Problem } from '@/api/client';

import type { SearchGroups, SearchStreamEvent } from './api';

/**
 * Where the answer to one query stands.
 *
 * - `waiting` — the stream is open, nothing has arrived yet.
 * - `semantic` — the lexical hits are in; related ones may still be appended.
 * - `done` — nothing more is coming: `done`, `semantic_unavailable`, an error, or a stream that
 *   ended without saying so.
 */
export type SearchPhase = 'idle' | 'waiting' | 'semantic' | 'done';

export interface SearchStreamState {
  /** The normalised query this answer belongs to. `''` is no search. */
  readonly q: string;
  readonly phase: SearchPhase;
  readonly lexical: SearchGroups | null;
  readonly semantic: SearchGroups | null;
  /** The embedder did not answer; the lexical hits are the whole answer. */
  readonly semanticUnavailable: boolean;
  readonly problem: Problem | null;
}

export const IDLE: SearchStreamState = {
  q: '',
  phase: 'idle',
  lexical: null,
  semantic: null,
  semanticUnavailable: false,
  problem: null,
};

export function waitingFor(q: string): SearchStreamState {
  return { ...IDLE, q, phase: 'waiting' };
}

export function apply(state: SearchStreamState, event: SearchStreamEvent): SearchStreamState {
  switch (event.event) {
    case 'lexical':
      // Always a stream's first event, so it starts the answer afresh: the same query asked
      // again must not carry the last stream's related hits under this one's exact ones.
      return { ...waitingFor(state.q), phase: 'semantic', lexical: event.groups };
    case 'semantic':
      return { ...state, semantic: event.groups };
    case 'semantic_unavailable':
      return { ...state, phase: 'done', semanticUnavailable: true };
    case 'done':
      return { ...state, phase: 'done' };
    case 'error':
      return { ...state, phase: 'done', problem: event.problem };
  }
}

/**
 * One event against the state held for `settled`, with both of the hook's guards.
 *
 * - An event for another query (anything but `error`, which carries no `q`) is dropped and
 *   `state` comes back unchanged: a superseded query's events never reach state (§5a).
 * - A held state that belongs to another query is not built on; the answer restarts at
 *   `waitingFor(settled)` before the event is applied.
 */
export function accept(
  settled: string,
  state: SearchStreamState,
  event: SearchStreamEvent,
): SearchStreamState {
  if (event.event !== 'error' && event.q !== settled) return state;
  return apply(state.q === settled ? state : waitingFor(settled), event);
}
